import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  BarChart2, ArrowLeft, TrendingUp, BookOpen, Layers, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const QBMAnalytics = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/question-bank/analytics');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
      // Set default stats if endpoint doesn't exist yet
      setStats({
        total_questions: 0,
        published: 0,
        draft: 0,
        archived: 0,
        mcq_questions: 0,
        structured_questions: 0,
        by_difficulty: { easy: 0, medium: 0, hard: 0 },
        by_board: [],
        by_subject: [],
        topics: 0,
        subtopics: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/qbm/dashboard')}
            className="flex items-center gap-2 text-orange-100 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Question Bank
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <BarChart2 className="w-8 h-8" />
              Question Bank Analytics
            </h1>
            <p className="mt-2 text-orange-200">Usage statistics and insights</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-sm text-slate-600">Total Questions</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{stats?.total_questions || 0}</p>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-slate-600">Published</span>
            </div>
            <p className="text-3xl font-bold text-green-600">{stats?.published || 0}</p>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Layers className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-600">Topics</span>
            </div>
            <p className="text-3xl font-bold text-purple-600">{stats?.topics || 0}</p>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-slate-600">Drafts</span>
            </div>
            <p className="text-3xl font-bold text-yellow-600">{stats?.draft || 0}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Question Types */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Question Types</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">MCQ</span>
                  <span className="text-sm font-medium text-slate-900">{stats?.mcq_questions || 0}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ 
                      width: `${stats?.total_questions ? (stats.mcq_questions / stats.total_questions * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Structured</span>
                  <span className="text-sm font-medium text-slate-900">{stats?.structured_questions || 0}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ 
                      width: `${stats?.total_questions ? (stats.structured_questions / stats.total_questions * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Difficulty Distribution */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Difficulty Distribution</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Easy</span>
                  <span className="text-sm font-medium text-green-600">{stats?.by_difficulty?.easy || 0}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ 
                      width: `${stats?.total_questions ? ((stats.by_difficulty?.easy || 0) / stats.total_questions * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Medium</span>
                  <span className="text-sm font-medium text-yellow-600">{stats?.by_difficulty?.medium || 0}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full transition-all"
                    style={{ 
                      width: `${stats?.total_questions ? ((stats.by_difficulty?.medium || 0) / stats.total_questions * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-600">Hard</span>
                  <span className="text-sm font-medium text-red-600">{stats?.by_difficulty?.hard || 0}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-red-500 rounded-full transition-all"
                    style={{ 
                      width: `${stats?.total_questions ? ((stats.by_difficulty?.hard || 0) / stats.total_questions * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Status Breakdown</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stats?.published || 0}</p>
                <p className="text-sm text-green-700">Published</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{stats?.draft || 0}</p>
                <p className="text-sm text-yellow-700">Draft</p>
              </div>
              <div className="p-4 bg-slate-100 rounded-lg">
                <p className="text-2xl font-bold text-slate-600">{stats?.archived || 0}</p>
                <p className="text-sm text-slate-700">Archived</p>
              </div>
            </div>
          </div>

          {/* Coverage */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Content Coverage</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Topics Covered</span>
                <span className="font-semibold text-slate-900">{stats?.topics || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Subtopics Covered</span>
                <span className="font-semibold text-slate-900">{stats?.subtopics || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Avg Questions/Topic</span>
                <span className="font-semibold text-slate-900">
                  {stats?.topics ? Math.round(stats.total_questions / stats.topics) : 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State Info */}
        {stats?.total_questions === 0 && (
          <div className="mt-8 bg-blue-50 rounded-xl p-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-blue-400 mb-4" />
            <h3 className="text-lg font-semibold text-blue-900 mb-2">No Questions Yet</h3>
            <p className="text-blue-700 mb-4">
              Start adding questions to see analytics and usage statistics here.
            </p>
            <button
              onClick={() => navigate('/qbm/question/new')}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all"
            >
              Add Your First Question
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QBMAnalytics;
